import { distanceM } from './distance.js';
import { matchToTrack } from './track.js';
export function prepareRoute(points) {
    if (points.length < 2) {
        return null;
    }
    const punkte = [];
    let kumuliert = 0;
    let vorher = null;
    for (const [i, p] of points.entries()) {
        if (vorher !== null) {
            kumuliert += distanceM(vorher, p);
        }
        punkte.push({
            seq: i,
            lat: p.lat,
            lon: p.lon,
            distanceM: kumuliert,
            recordedAt: 0,
            travelMs: 0,
        });
        vorher = p;
    }
    return { points: punkte, lengthM: kumuliert };
}
export function locateOnRoute(line, point, options) {
    const treffer = matchToTrack(line.points, point, {
        at: options.at,
        hint: options.hint,
        headingDeg: options.headingDeg,
        speedMps: options.speedMps,
        /*
         * Anders als beim Rückstand darf die Zuordnung hier mitwandern, auch wenn
         * das Fahrzeug einige hundert Meter neben der Route fährt. Die Frage ist
         * nicht „wo warst du zuletzt nachweislich auf der Strecke" (A-60), sondern
         * „wie weit ist es noch bis zum Treffpunkt" — und wer parallel durch den
         * Ort fährt, will keine Entfernung ab dem Ortseingang.
         */
        advanceOnlyOnTrack: false,
    });
    if (treffer === null) {
        return null;
    }
    return {
        distanceM: treffer.distanceM,
        offRouteM: treffer.offTrackM,
        withinHint: treffer.withinHint,
    };
}
/**
 * Wo auf der Route ein Ort liegt — ohne Verlauf, ohne Vorgeschichte.
 *
 * Für den Planer am Schreibtisch (Stufe 3, Schritt 4). Er klickt auf die Linie,
 * und die Frage lautet schlicht „welcher Kilometer ist das". Es gibt kein
 * Fahrzeug, dessen bisheriger Weg die Mehrdeutigkeit eines Rundkurses auflösen
 * könnte — und es braucht auch keines: Der Planer sieht, wohin er klickt.
 *
 * Auf einem Rundkurs, der zweimal über dieselbe Straße führt, gewinnt damit die
 * räumlich nächste Vorbeifahrt. Das ist hier richtig und während der Fahrt
 * falsch; deshalb steht es neben `locateOnRoute` und nicht darin.
 */
export function markOnRoute(line, point) {
    const treffer = matchToTrack(line.points, point, { at: 0, advanceOnlyOnTrack: false });
    if (treffer === null) {
        return null;
    }
    return {
        distanceM: treffer.distanceM,
        offRouteM: treffer.offTrackM,
        lat: treffer.lat,
        lon: treffer.lon,
    };
}
/**
 * Der Ort auf der Linie nach der angegebenen Streckenlänge.
 *
 * Zwischen zwei Punkten wird geradlinig geteilt. Über die paar Meter eines
 * Abschnitts ist der Unterschied zur Kugel kleiner als jede GPS-Ungenauigkeit;
 * über die ganze Strecke wird nie interpoliert, sondern immer nur innerhalb
 * eines Abschnitts.
 */
function ortBei(punkte, meter) {
    for (let i = 0; i < punkte.length - 1; i += 1) {
        const a = punkte[i];
        const b = punkte[i + 1];
        if (a === undefined || b === undefined || meter < a.distanceM || meter > b.distanceM) {
            continue;
        }
        const spanne = b.distanceM - a.distanceM;
        const t = spanne <= 0 ? 0 : (meter - a.distanceM) / spanne;
        return { lat: a.lat + (b.lat - a.lat) * t, lon: a.lon + (b.lon - a.lon) * t };
    }
    return null;
}
/**
 * Die Enden einer Route abschneiden (Stufe 3, Schritt 4).
 *
 * ## Warum hier und nicht in der Datenbank
 *
 * PostGIS könnte es mit `ST_LineSubstring`, aber nur nach **Anteil** an der
 * Linie — und der Anteil wird in der Ebene gerechnet. Auf Längen- und
 * Breitengraden ist die Ebene verzerrt; aus „bei 1000 Metern schneiden" würde
 * je nach Verlauf der Strecke etwas anderes. Dieselbe Falle, die
 * `LAGE_AUF_DER_ROUTE` im Zugriff eigens umgeht.
 *
 * `prepareRoute` hat die Längen dagegen bereits auf der Kugel gerechnet, Punkt
 * für Punkt. In Metern zu schneiden ist damit genau das, was es verspricht.
 * Und es ist ohne Datenbank prüfbar — wie alles in diesem Paket.
 *
 * ## Was dabei erhalten bleibt
 *
 * **Jeder Punkt dazwischen, unverändert.** Der Auftrag, Abschnitt 2.2: Eine
 * GPX-Datei hält eine bestimmte Straßenwahl fest, und die ist der Inhalt einer
 * Oldtimer-Ausfahrt. Neu gerechnet wird nichts; die beiden Enden werden auf
 * ihrem Abschnitt geteilt, mehr geschieht nicht.
 *
 * Liefert eine leere Liste, wenn zwischen den Marken keine Strecke bleibt. Das
 * ist kein Fehler dieser Rechnung, sondern eine Angabe, mit der sich nicht
 * fahren lässt — der Aufrufer sagt es dem Planer.
 */
export function trimRoute(line, vonM, bisM) {
    const von = Math.min(Math.max(vonM, 0), line.lengthM);
    const bis = Math.min(Math.max(bisM, 0), line.lengthM);
    if (bis <= von) {
        return [];
    }
    const anfang = ortBei(line.points, von);
    const ende = ortBei(line.points, bis);
    if (anfang === null || ende === null) {
        return [];
    }
    /*
     * Streng größer und streng kleiner: Fällt eine Marke genau auf einen Punkt
     * der Linie, ist der geteilte Ort derselbe Ort — er stünde sonst zweimal da.
     */
    const dazwischen = line.points
        .filter((p) => p.distanceM > von && p.distanceM < bis)
        .map((p) => ({ lat: p.lat, lon: p.lon }));
    return [anfang, ...dazwischen, ende];
}
/**
 * Der Ort auf der Route nach so vielen Metern.
 *
 * Die Umkehrung von `markOnRoute`: Dort wird zu einem Ort die Strecke gesucht,
 * hier zu einer Strecke der Ort. Gebraucht wird sie für die Anker beim
 * Ersetzen eines Stücks — der Routingdienst braucht Koordinaten, der Planer
 * hat auf Kilometer gezeigt.
 */
export function pointOnRouteAt(line, meterM) {
    return ortBei(line.points, Math.min(Math.max(meterM, 0), line.lengthM));
}
/**
 * Ein Stück der Route durch ein anderes ersetzen (Stufe 3, Schritt 4).
 *
 * ## Warum einsetzen und nicht neu rechnen
 *
 * Der Auftrag, Abschnitt 3 von `docs/stufe-3.md`: „Ändern heißt einsetzen,
 * nicht neu rechnen." Eine GPX-Datei hält eine bestimmte Straßenwahl fest —
 * den Umweg über die Nebenstrecke, die Allee, die Serpentine. Würde beim
 * Ändern eines Stücks die ganze Route neu berechnet, käme der vernünftige Weg
 * zurück, und der vernünftige Weg ist die Bundesstraße.
 *
 * **Alles außerhalb der beiden Anker bleibt deshalb Punkt für Punkt stehen.**
 * Hier wird nur zusammengesetzt; woher das Ersatzstück kommt, weiß diese
 * Rechnung nicht und muss es nicht wissen.
 *
 * ## Die Nahtstellen
 *
 * Kopf und Schwanz enden beziehungsweise beginnen **genau** auf dem Anker, den
 * der Planer gesetzt hat — dafür wird ihr letzter Abschnitt geteilt, wie beim
 * Abschneiden. Das Ersatzstück beginnt dort, wo der Routingdienst den Anker auf
 * sein Straßennetz gelotet hat, also ein paar Meter daneben. Diese wenigen
 * Meter bleiben sichtbar in der Linie stehen, statt geglättet zu werden: Sie
 * sind die Wahrheit über die Naht, und wer sie glättet, verschiebt einen der
 * beiden Punkte, ohne es zu sagen.
 *
 * Liefert eine leere Liste, wenn die Angaben keine Strecke ergeben — der
 * Aufrufer sagt es dem Planer.
 */
export function spliceRoute(line, vonM, bisM, ersatz) {
    const von = Math.min(Math.max(vonM, 0), line.lengthM);
    const bis = Math.min(Math.max(bisM, 0), line.lengthM);
    if (bis <= von || ersatz.length < 2) {
        return [];
    }
    const kopf = trimRoute(line, 0, von);
    const schwanz = trimRoute(line, bis, line.lengthM);
    return [...kopf, ...ersatz, ...schwanz];
}
/**
 * Endet die Punktfolge genau dort, wo sie anfängt?
 *
 * Die Frage stellt sich an drei Stellen der Planung — auf der Karte, im Umriss
 * und in dem Satz darunter —, und sie muss überall dieselbe Antwort bekommen.
 *
 * **Genau**, nicht ungefähr: `closeGap` hängt den Anfangspunkt selbst an, der
 * Abstand ist danach null und nicht klein. Eine Schwelle wäre hier eine
 * erfundene Zahl und würde eine Runde, die aus der Datei mit zwei Metern
 * Abstand kommt, fälschlich für geschlossen erklären.
 */
export function isClosedRoute(points) {
    const erster = points[0];
    const letzter = points[points.length - 1];
    return points.length >= 2 && erster !== undefined && letzter !== undefined
        ? distanceM(erster, letzter) === 0
        : false;
}
//# sourceMappingURL=route.js.map